import {
  Box, MenuItem, Select, Stack, Typography,
} from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded'
import { useQuery } from '@tanstack/react-query'

import { api } from '../lib/api'

interface Props {
  /// Currently-selected customer id. `null` means "All customers"
  /// (shared/org-wide) for catalog mode, or "no selection yet" for
  /// required mode.
  value: string | null
  onChange: (id: string | null) => void
  /// `required` — sites and other strict resources: must pick a
  /// specific customer; no "All customers" option.
  /// `catalog` — vendors / checklists / workflows / SLA / categories /
  /// parts: "All customers" is the default and means org-wide.
  mode: 'required' | 'catalog'
  /// Label shown above the picker. Defaults match the standard
  /// "Customer" wording but the caller can override for context
  /// (e.g. "Customer scope" on catalog forms).
  label?: string
  helperText?: string
  disabled?: boolean
  size?: 'small' | 'medium'
}

/// Reusable customer-scope picker.
///
/// Two modes:
///   • required — site create, asset create (when no site), etc.
///                Renders the customer list; no "All customers".
///   • catalog  — vendors, checklist templates, workflows, SLA
///                policies, asset categories, parts. Renders an
///                "All customers" option at the top representing
///                NULL customer_id (shared across the whole org).
///
/// Auto-hides itself for single-customer accounts in catalog mode —
/// when there's only one customer in the org there's no choice to
/// make. In required mode we still render it (with the one option
/// pre-selected) so the saved value is explicit on the row.
export function CustomerScopePicker({
  value, onChange, mode, label, helperText, disabled, size = 'small',
}: Props) {
  const customersQ = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list(),
    staleTime: 5 * 60_000,
  })
  const customers = Array.isArray(customersQ.data) ? customersQ.data : []

  // Hide entirely in catalog mode for single-customer orgs — leaving
  // the field NULL is implicit and matches the only customer.
  if (mode === 'catalog' && customersQ.isSuccess && customers.length <= 1) {
    return null
  }
  if (customersQ.isLoading) return null

  const displayLabel = label ?? (mode === 'required' ? 'Customer' : 'Customer scope')
  // Select needs a non-null value; encode "All customers" as the
  // empty string and translate at the boundary.
  const selectValue = value ?? ''

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          {displayLabel}{mode === 'required' && <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>}
        </Typography>
      </Stack>
      <Select
        value={selectValue}
        onChange={(e) => {
          const v = e.target.value
          onChange(v === '' ? null : v)
        }}
        size={size}
        fullWidth
        disabled={disabled}
        displayEmpty
        renderValue={(v) => {
          if (!v) {
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                {mode === 'catalog' ? (
                  <>
                    <AllInclusiveRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 13.5 }}>All customers (shared)</Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 13.5, color: 'text.disabled' }}>
                    Select a customer
                  </Typography>
                )}
              </Stack>
            )
          }
          const c = customers.find((x) => x.id === v)
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <BusinessOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: 13.5 }}>{c?.name ?? '—'}</Typography>
            </Stack>
          )
        }}
      >
        {mode === 'catalog' && (
          <MenuItem value="">
            <Stack direction="row" alignItems="center" spacing={1}>
              <AllInclusiveRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>All customers</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  Shared org-wide — available to every customer
                </Typography>
              </Box>
            </Stack>
          </MenuItem>
        )}
        {customers.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <BusinessOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{c.name}</Typography>
                {typeof c.site_count === 'number' && (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {c.site_count} site{c.site_count === 1 ? '' : 's'}
                  </Typography>
                )}
              </Box>
            </Stack>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  )
}
